// Occasional one-question prompts that refine the profile as the app is used. Each rule needs
// repeated evidence, never one action; a prompt that was answered or dismissed is never asked again;
// at most one shows, at most once a day, and the user can turn them off.

import type { Feedback, Profile } from '../state/schema';

export interface PromptOption {
  label: string;
  /** Profile changes this answer makes. Empty means "no change". */
  patch: (p: Profile) => Partial<Profile>;
}

export interface PersonalizationPrompt {
  id: string;
  question: string;
  why: string;
  options: PromptOption[];
}

export interface PromptContext {
  profile: Profile;
  cookedCount: number;
  feedback: Feedback[];
  kitchenCount: number;
  hasPlan: boolean;
  /** From learn.ts: 0..1 strength of a "prefers quick dinners" pattern. */
  preferQuick: number;
  onboardedAt: string | null;
  now: Date;
}

const DAY = 86_400_000;
const keep = () => ({});

const RULES: { prompt: PersonalizationPrompt; when: (c: PromptContext) => boolean }[] = [
  {
    when: (c) => c.profile.maxMinutes > 30
      && (c.feedback.filter((f) => f.type === 'not_today' && f.reason === 'too_long').length >= 2 || c.preferQuick >= 0.5),
    prompt: {
      id: 'quicker_meals',
      question: 'Would you prefer meals that take less time?',
      why: 'You’ve passed on longer dinners a few times.',
      options: [
        { label: 'Yes, 30 minutes or less', patch: () => ({ maxMinutes: 30 }) },
        { label: 'No, time is fine', patch: keep },
      ],
    },
  },
  {
    when: (c) => c.kitchenCount >= 5 && !c.profile.priorities.includes('less_waste') && !c.profile.frustrations.includes('waste'),
    prompt: {
      id: 'use_what_i_have',
      question: 'Should we put recipes that use what you already have first?',
      why: 'You’ve added a few things to your kitchen.',
      options: [
        { label: 'Yes, use my kitchen first', patch: (p) => ({ priorities: [...p.priorities.filter((x) => x !== 'less_waste'), 'less_waste' as const].slice(-3) }) },
        { label: 'No thanks', patch: keep },
      ],
    },
  },
  {
    when: (c) => c.hasPlan && c.profile.budget === 'moderate' && !c.profile.frustrations.includes('cost'),
    prompt: {
      id: 'weekly_budget',
      question: 'How much should your weekly plan cost?',
      why: 'You’ve started planning your week.',
      options: [
        { label: 'Keep it low', patch: () => ({ budget: 'tight' }) },
        { label: 'Moderate is fine', patch: () => ({ budget: 'moderate' }) },
        { label: 'Flexible', patch: () => ({ budget: 'flexible' }) },
      ],
    },
  },
  {
    when: (c) => c.cookedCount >= 3 && !c.profile.goals.length && !c.profile.hideNumbers,
    prompt: {
      id: 'nutrition_goal',
      question: 'Would you like picks tailored to a nutrition goal?',
      why: 'You’ve cooked a few dinners now.',
      options: [
        { label: 'Eat more balanced meals', patch: () => ({ goals: ['balanced'] }) },
        { label: 'Build muscle', patch: () => ({ goals: ['muscle'] }) },
        { label: 'No specific goal', patch: () => ({ goals: ['none'] }) },
      ],
    },
  },
  {
    when: (c) => c.profile.heat !== 'none' && c.feedback.filter((f) => f.type === 'not_today' && f.reason === 'too_spicy').length >= 2,
    prompt: {
      id: 'less_spice',
      question: 'Should we keep things mild?',
      why: 'A couple of dinners were too spicy.',
      options: [
        { label: 'Yes, no heat', patch: () => ({ heat: 'none' }) },
        { label: 'A little is fine', patch: () => ({ heat: 'mild' }) },
      ],
    },
  },
  {
    when: (c) => !c.profile.likes.length && !!c.onboardedAt && c.now.getTime() - new Date(c.onboardedAt).getTime() >= DAY,
    prompt: {
      id: 'food_likes',
      question: 'Which of these do you enjoy most?',
      why: 'It helps us pick dinners you’ll want to make.',
      options: [
        { label: 'Rice dishes', patch: () => ({ likes: ['rice'] }) },
        { label: 'Pasta and noodles', patch: () => ({ likes: ['pasta_noodles'] }) },
        { label: 'Chicken and meat', patch: () => ({ likes: ['meat'] }) },
        { label: 'Vegetarian', patch: () => ({ likes: ['vegetarian'] }) },
      ],
    },
  },
];

/** The one prompt to show now, or null. */
export function nextPrompt(c: PromptContext): PersonalizationPrompt | null {
  const state = c.profile.prompts;
  if (!state.enabled) return null;
  if (state.lastShownAt && c.now.getTime() - new Date(state.lastShownAt).getTime() < DAY) return null;
  return RULES.find((r) => !state.done[r.prompt.id] && r.when(c))?.prompt ?? null;
}

export const PROMPT_IDS = RULES.map((r) => r.prompt.id);
